import React, { useState, useEffect } from 'react';
import { formatCurrency, colors } from '../theme';
import api from '../services/api';

const Settings: React.FC = () => {
  const [chainData, setChainData] = useState<Record<string, { primary: string; secondary: string; autoApproveLimit: number }>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.approvals.getChains()
      .then(res => {
        const data = res.data || res;
        if (data && typeof data === 'object') setChainData(data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <div className="header">
        <div className="header-left">
          <div>
            <h2>Settings</h2>
            <p>System configuration & approval rules</p>
          </div>
        </div>
        <div className="header-right">
          <button className="header-btn primary">💾 Save Changes</button>
        </div>
      </div>

      <div className="content">
        {loading && <div style={{ padding: 40, textAlign: 'center', fontSize: 16, color: '#94A3B8' }}>Loading...</div>}
        <div className="grid-2">
          {/* Approval Chains */}
          <div className="card">
            <div className="card-header">
              <h3>Approval Chains</h3>
            </div>
            <div className="card-body">
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Primary Approver</th>
                      <th>Secondary</th>
                      <th>Auto-Approve Limit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(chainData).map(([cat, chain]) => (
                      <tr key={cat}>
                        <td>
                          <span style={{
                            padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 700,
                            background: colors.goldSoft, color: colors.goldDark
                          }}>{cat.replace('_', ' ')}</span>
                        </td>
                        <td style={{ fontSize: 13 }}>{chain.primary}</td>
                        <td style={{ fontSize: 13 }}>{chain.secondary}</td>
                        <td className="amount">{formatCurrency(chain.autoApproveLimit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* SLA Configuration */}
          <div className="card">
            <div className="card-header">
              <h3>SLA Configuration</h3>
            </div>
            <div className="card-body padded">
              <div className="form-group">
                <label className="form-label">Standard LOC SLA (minutes)</label>
                <input className="form-input" type="number" defaultValue={30} />
              </div>
              <div className="form-group">
                <label className="form-label">Auto-Escalation Timer (minutes)</label>
                <input className="form-input" type="number" defaultValue={15} />
              </div>
              <div className="form-group">
                <label className="form-label">Emergency Ratification Window (hours)</label>
                <input className="form-input" type="number" defaultValue={2} />
              </div>
              <div className="form-group">
                <label className="form-label">Hospital Verification Cache (days)</label>
                <input className="form-input" type="number" defaultValue={30} />
              </div>
              <div className="form-group">
                <label className="form-label">Name Match Threshold (%)</label>
                <input className="form-input" type="number" defaultValue={85} />
              </div>
            </div>
          </div>
        </div>

        <div className="grid-2">
          {/* Payment Config */}
          <div className="card">
            <div className="card-header">
              <h3>Payment Configuration</h3>
            </div>
            <div className="card-body padded">
              <div className="form-group">
                <label className="form-label">Advance Payment (%)</label>
                <input className="form-input" type="number" defaultValue={80} />
              </div>
              <div className="form-group">
                <label className="form-label">Final Settlement (%)</label>
                <input className="form-input" type="number" defaultValue={20} />
              </div>
              <div className="form-group">
                <label className="form-label">Excess Refund Window (days)</label>
                <input className="form-input" type="number" defaultValue={7} />
              </div>
              <div className="form-group">
                <label className="form-label">Shortfall LOC Window (hours)</label>
                <input className="form-input" type="number" defaultValue={24} />
              </div>
            </div>
          </div>

          {/* Notification Config */}
          <div className="card">
            <div className="card-header">
              <h3>Notification Settings</h3>
            </div>
            <div className="card-body padded">
              {[
                { label: 'WhatsApp Notifications', desc: 'LOC PDFs, status updates, OTP', checked: true },
                { label: 'SMS Notifications', desc: 'OTP fallback, critical alerts', checked: true },
                { label: 'Push Notifications', desc: 'Real-time status changes', checked: true },
                { label: 'Email Notifications', desc: 'Daily digest, SLA breach reports', checked: false },
                { label: 'SLA Breach Alerts', desc: 'Notify CEO on SLA breach', checked: true },
                { label: 'Emergency Auto-Alert', desc: 'Immediate notification to approver', checked: true },
              ].map(item => (
                <div key={item.label} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '10px 0', borderBottom: `1px solid ${colors.borderLight}`
                }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{item.label}</div>
                    <div style={{ fontSize: 12, color: colors.textMuted }}>{item.desc}</div>
                  </div>
                  <label style={{ position: 'relative', display: 'inline-block', width: 44, height: 24 }}>
                    <input type="checkbox" defaultChecked={item.checked} style={{ opacity: 0, width: 0, height: 0 }} />
                    <span style={{
                      position: 'absolute', cursor: 'pointer', inset: 0,
                      background: item.checked ? colors.success : colors.borderMain,
                      borderRadius: 12, transition: '0.3s',
                    }}>
                      <span style={{
                        position: 'absolute', content: '""',
                        height: 18, width: 18, left: item.checked ? 22 : 3, bottom: 3,
                        background: '#fff', borderRadius: 9, transition: '0.3s',
                      }} />
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* System Info */}
        <div className="card">
          <div className="card-header">
            <h3>System Information</h3>
          </div>
          <div className="card-body padded">
            <div className="grid-3">
              {[
                ['Application', 'AIS Officers Healthcare Admin v1.0'],
                ['Organization', 'APCFSS — Govt. of Andhra Pradesh'],
                ['Trust', 'Dr. NTR Vaidya Seva Trust'],
                ['Platform', 'React 18 + TypeScript'],
                ['Mobile App', 'React Native (Expo SDK)'],
                ['Database', 'PostgreSQL (Planned)'],
                ['Hosting', 'AP State Data Center'],
                ['Security', 'AES-256 + TLS 1.3'],
                ['Compliance', 'IT Act 2000 · DPDP Act 2023'],
              ].map(([label, value]) => (
                <div className="detail-row" key={label}>
                  <span className="detail-label">{label}</span>
                  <span className="detail-value">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Settings;
